// About — Fair Hospitality manifesto page.
// Magician territory per Brand PDF page 2; Fraunces italic used as deliberate
// punctuation in three well-spaced moments (hero · pull-quote · closing line),
// never in consecutive sections per the discipline rule on page 5.

import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/icons.jsx";
import { useLocaleNav } from "../lib/useLocaleNav.js";
import { goToBooking } from "../lib/handoff.js";

export default function About() {
  const go = useLocaleNav();
  return (
    <main>
      <Hero />
      <WhatWeDo />
      <ManifestoPullQuote />
      <FinalCTA go={go} />
    </main>
  );
}

function AmberEyebrow({ label }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--marigold)" }}>
      <span style={{ width: 22, height: 1, background: "var(--marigold)" }} />
      {label}
    </div>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section style={{ background: "var(--ink)", color: "var(--bg)", padding: "160px 0 180px" }}>
      <div className="container">
        <AmberEyebrow label={t("about.hero.eyebrow")} />
        <h1 style={{ margin: "32px 0 0", fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, fontSize: "clamp(56px, 8vw, 144px)", lineHeight: 0.95, letterSpacing: "-0.045em", color: "var(--bg)", maxWidth: 1300 }}>
          {t("about.hero.headline")}{" "}
          <span style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400, color: "var(--marigold)" }}>{t("about.hero.headlineItalic")}</span>
        </h1>
        <p style={{ marginTop: 48, maxWidth: 820, fontSize: "clamp(20px, 1.8vw, 26px)", lineHeight: 1.5, color: "rgba(255,255,255,.82)", fontFamily: "Inter, sans-serif" }}>
          {t("about.hero.intro")}
        </p>
        <p style={{ marginTop: 24, fontSize: "clamp(15px, 1.1vw, 18px)", lineHeight: 1.5, color: "rgba(255,255,255,.55)", fontFamily: "Inter, sans-serif", letterSpacing: ".02em" }}>
          {t("about.hero.sub")}
        </p>
      </div>
    </section>
  );
}

function WhatWeDo() {
  const { t } = useTranslation();
  const guests = t("about.whatWeDo.guests", { returnObjects: true });
  return (
    <section style={{ background: "var(--bg)", padding: "160px 0", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--persimmon)" }}>
          <span style={{ width: 22, height: 1, background: "var(--persimmon)" }} />
          {t("about.whatWeDo.eyebrow")}
        </div>
        <h2 className="tt-h tt-h-xl" style={{ marginTop: 32, maxWidth: 1200 }}>
          {t("about.whatWeDo.headline")}{" "}
          <span style={{ color: "var(--persimmon)" }}>{t("about.whatWeDo.headlineSpan")}</span>
        </h2>
        <p style={{ marginTop: 40, maxWidth: 880, fontSize: "clamp(18px, 1.4vw, 22px)", lineHeight: 1.6, color: "var(--ink-2)", fontFamily: "Inter, sans-serif" }}>
          {t("about.whatWeDo.intro")}
        </p>

        <div style={{ marginTop: 80, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0, borderTop: "1px solid var(--ink)" }} className="beat-grid">
          {guests.map((g, i) => (
            <div key={i} style={{ padding: "40px 28px", borderRight: i < 2 ? "1px solid var(--line)" : 0 }} className="beat-cell">
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--persimmon)" }}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <p style={{ margin: "20px 0 0", fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: "clamp(20px, 1.8vw, 26px)", lineHeight: 1.35, color: "var(--ink)" }}>{g}</p>
            </div>
          ))}
        </div>

        <p style={{ marginTop: 96, maxWidth: 1100, fontSize: "clamp(28px, 3.2vw, 48px)", lineHeight: 1.2, color: "var(--ink)", fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, letterSpacing: "-0.02em" }}>
          {t("about.whatWeDo.outroPrefix")}{" "}
          <span style={{ color: "var(--persimmon)" }}>{t("about.whatWeDo.outroSpan")}</span>
          {" "}{t("about.whatWeDo.outroSuffix")}
        </p>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .beat-grid { grid-template-columns: 1fr !important; }
          .beat-cell { border-right: 0 !important; border-bottom: 1px solid var(--line); }
          .beat-cell:last-child { border-bottom: 0; }
        }
      `}</style>
    </section>
  );
}

function ManifestoPullQuote() {
  const { t } = useTranslation();
  return (
    <section style={{ background: "var(--bg)", padding: "160px 0", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--marigold)" }}>
          <span style={{ width: 22, height: 1, background: "var(--marigold)" }} />
          {t("about.manifesto.eyebrow")}
        </div>
        <div className="manifesto-card">
          <div className="manifesto-body tt-edit">
            <p>{t("about.manifesto.p1")}</p>
            <p>{t("about.manifesto.p2")}</p>
            <p>{t("about.manifesto.p3")}</p>
            <p className="manifesto-now">{t("about.manifesto.nowPrefix")} <span style={{ color: "var(--marigold)" }}>{t("about.manifesto.nowEmphasis")}</span></p>
          </div>
          <div className="manifesto-sig">
            <img src="/assets/signature.webp" alt={t("about.manifesto.signatureAlt")} loading="lazy" decoding="async" />
          </div>
        </div>
      </div>
      <style>{`
        .manifesto-card {
          margin-top: 40px;
          background: var(--bg-2);
          border-left: 12px solid var(--marigold);
          border-radius: 4px;
          padding: 88px 96px 72px;
        }
        .manifesto-body {
          font-size: clamp(22px, 2.5vw, 34px);
          line-height: 1.36;
          color: var(--ink);
          max-width: 1020px;
        }
        .manifesto-body p { margin: 0 0 26px; }
        .manifesto-body p:last-child { margin-bottom: 0; }
        .manifesto-now { margin-top: 36px !important; }
        .manifesto-sig {
          margin-top: 56px;
          height: 72px;
          display: flex;
          align-items: center;
        }
        .manifesto-sig img {
          height: 100%;
          width: auto;
          display: block;
          mix-blend-mode: multiply;
        }
        @media (max-width: 900px) {
          .manifesto-card { padding: 48px 32px 40px; border-left-width: 8px; }
          .manifesto-sig { margin-top: 44px; height: 60px; }
        }
      `}</style>
    </section>
  );
}

function Mission() {
  return (
    <section style={{ background: "var(--bg)", padding: "160px 0", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <AmberEyebrow label="Our mission" />
        <h2 className="tt-h tt-h-xl" style={{ marginTop: 32, maxWidth: 1200 }}>
          Help hospitality teams see problems while they can still fix them.
        </h2>
        <p style={{ marginTop: 56, fontSize: "clamp(28px, 3.2vw, 48px)", lineHeight: 1.22, fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.02em", maxWidth: 1100 }}>
          Not tomorrow. Not after checkout.{" "}
          <span style={{ color: "var(--persimmon)" }}>Now.</span>
        </p>
      </div>
    </section>
  );
}

function Vision() {
  const points = [
    "guests are heard instantly",
    "staff can react in real time",
    "experiences improve while they happen",
    "feedback becomes operational, not historical"
  ];
  return (
    <section style={{ background: "var(--ink)", color: "var(--bg)", padding: "180px 0" }}>
      <div className="container">
        <AmberEyebrow label="Our vision" />
        <h2 style={{ margin: "32px 0 0", fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, fontSize: "clamp(48px, 6.5vw, 112px)", lineHeight: 0.95, letterSpacing: "-0.04em", color: "var(--bg)", maxWidth: 1300 }}>
          A hospitality industry where:
        </h2>
        <ul style={{ margin: "80px 0 0", padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 0, borderTop: "1px solid rgba(255,255,255,.18)" }} className="vis-grid">
          {points.map((p, i) => (
            <li key={i} style={{
              padding: "44px 28px",
              borderRight: i % 2 === 0 ? "1px solid rgba(255,255,255,.18)" : 0,
              borderBottom: i < 2 ? "1px solid rgba(255,255,255,.18)" : 0,
              display: "flex", gap: 18, alignItems: "flex-start"
            }} className="vis-cell">
              <span style={{ flexShrink: 0, marginTop: 14, width: 9, height: 9, borderRadius: "50%", background: "var(--marigold)" }} />
              <span style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: "clamp(22px, 2.1vw, 30px)", lineHeight: 1.32, color: "var(--bg)" }}>{p}</span>
            </li>
          ))}
        </ul>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .vis-grid { grid-template-columns: 1fr !important; }
          .vis-cell { border-right: 0 !important; border-bottom: 1px solid rgba(255,255,255,.18) !important; }
          .vis-cell:last-child { border-bottom: 0 !important; }
        }
      `}</style>
    </section>
  );
}

function WhatDrivesUs() {
  const moments = [
    "A good dinner saved at the right time.",
    "A frustrated guest turned into a happy one.",
    "A team that knows exactly what's happening on the floor."
  ];
  return (
    <section style={{ background: "var(--bg-2)", padding: "180px 0" }}>
      <div className="container">
        <AmberEyebrow label="What drives us" />
        <h2 className="tt-h tt-h-xl" style={{ marginTop: 32, maxWidth: 1100 }}>
          We care about moments.
        </h2>

        <div style={{ marginTop: 88, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 32 }} className="moments-grid">
          {moments.map((m, i) => (
            <div key={i} style={{ padding: "32px 0", borderTop: "1px solid var(--ink)" }} className="moments-cell">
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--marigold)" }}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <p style={{ margin: "20px 0 0", fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: "clamp(18px, 1.5vw, 22px)", lineHeight: 1.45, color: "var(--ink)", maxWidth: 380 }}>{m}</p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 140, display: "flex", gap: 32 }}>
          <div style={{ width: 4, background: "var(--marigold)", borderRadius: 2, flexShrink: 0 }} />
          <p style={{ margin: 0, fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400, fontSize: "clamp(40px, 5.6vw, 88px)", lineHeight: 1.12, color: "var(--ink)", maxWidth: 1000 }}>
            Small fixes. Big impact.
          </p>
        </div>
      </div>
      <style>{`@media (max-width: 900px){.moments-grid{grid-template-columns:1fr !important}}`}</style>
    </section>
  );
}

function FinalCTA({ go }) {
  const { t } = useTranslation();
  return (
    <section className="bg-bold" style={{ padding: "180px 0" }}>
      <div className="container">
        <h2 className="tt-h tt-h-xl" style={{ color: "var(--ink)" }}>
          {t("about.finalCta")}
        </h2>
        <div style={{ marginTop: 40 }}>
          <a href="#" className="btn btn-lg" onClick={(e) => { e.preventDefault(); goToBooking(); }} style={{ background: "var(--ink)", color: "var(--bg)" }}>
            {t("common.bookDemo")} <Icon name="arrow-right" size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
